import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router";
import _ from "lodash";

import { CloseIcon, SettingsOutlinedIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";

import Box from "components/Box";
import Button from "components/Button";
import Drawer from "components/Drawer";
import IconButton from "components/IconButton";
import Paper from "components/Paper";
import SidepanelHeader from "components/SidepanelHeader";
import Stack from "components/Stack";
import Text from "components/Text";

import AssetManagementToggle from "entities/AssetManagement/components/AssetManagementToggle";
import SettingsCategory from "entities/AssetManagement/components/SettingsCategory";
import {
  AMColumn,
  AMResolution,
  AMVarianceDisplayMode,
} from "entities/AssetManagement/constants";
import { useAssetManagementData } from "entities/AssetManagement/sdk";
import {
  extractLatestDatasetByAMColumn,
  generateAllRowsFromNewAMDataStructure,
} from "entities/AssetManagement/utils";

interface IAssetManagementTableSettings {
  columns: Array<AMColumn>;
  setColumns: Dispatch<SetStateAction<Array<AMColumn>>>;
  resolution: AMResolution;
  setResolution: Dispatch<SetStateAction<AMResolution>>;
  varianceDisplayMode: Array<AMVarianceDisplayMode>;
  setVarianceDisplayMode: Dispatch<
    SetStateAction<Array<AMVarianceDisplayMode>>
  >;
  showYearToDateColumn: boolean;
  setShowYearToDateColumn: Dispatch<SetStateAction<boolean>>;
  showSubItems: boolean;
  hideEmptyRows: boolean;
  setShowSubItems: Dispatch<SetStateAction<boolean>>;
  setHideEmptyRows: Dispatch<SetStateAction<boolean>>;
}

const AssetManagementTableSettings = ({
  columns,
  setColumns,
  resolution,
  setResolution,
  varianceDisplayMode,
  setVarianceDisplayMode,
  showYearToDateColumn,
  setShowYearToDateColumn,
  showSubItems,
  hideEmptyRows,
  setShowSubItems,
  setHideEmptyRows,
}: IAssetManagementTableSettings) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localColumns, setLocalColumns] = useState(columns);
  const [localResolution, setLocalResolution] = useState(resolution);
  const [localVarianceDisplayMode, setLocalVarianceDisplayMode] =
    useState(varianceDisplayMode);
  const [localShowYearToDateColumn, setLocalShowYearToDateColumn] =
    useState(showYearToDateColumn);
  const [localShowSubItems, setLocalShowSubItems] = useState(showSubItems);
  const [localHideEmptyRows, setLocalHideEmptyRows] = useState(hideEmptyRows);

  const params = useParams<{
    dealId: string;
  }>();
  const dealId = Number(params.dealId);

  const { data } = useAssetManagementData({
    dealId,
  });

  useEffect(() => {
    if (!isOpen) {
      setLocalColumns(columns);
      setLocalResolution(resolution);
      setLocalVarianceDisplayMode(varianceDisplayMode);
      setLocalShowYearToDateColumn(showYearToDateColumn);
      setLocalShowSubItems(showSubItems);
      setLocalHideEmptyRows(hideEmptyRows);
    }
  }, [
    columns,
    resolution,
    isOpen,
    varianceDisplayMode,
    showYearToDateColumn,
    hideEmptyRows,
    showSubItems,
  ]);

  const handleClick = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleApply = useCallback(() => {
    setColumns(localColumns);
    setResolution(localResolution);
    setVarianceDisplayMode(localVarianceDisplayMode);
    setShowYearToDateColumn(localShowYearToDateColumn);
    setShowSubItems(localShowSubItems);
    setHideEmptyRows(localHideEmptyRows);
    setIsOpen(false);
  }, [
    setColumns,
    setResolution,
    localColumns,
    localResolution,
    localShowYearToDateColumn,
    localVarianceDisplayMode,
    setVarianceDisplayMode,
    setShowYearToDateColumn,
    localHideEmptyRows,
    localShowSubItems,
    setHideEmptyRows,
    setShowSubItems,
  ]);

  const handleResolutionChange = useCallback(
    (resolutionString: AMResolution) => {
      setLocalResolution(resolutionString);

      if (
        resolutionString === AMResolution.MONTHLY &&
        localColumns.filter((column) => column !== AMColumn.AUDITED).length ===
          0
      ) {
        setLocalColumns((currentColumns) =>
          _.xor(currentColumns, [AMColumn.BUDGETED, AMColumn.ACTUAL])
        );
      }
    },
    [localColumns]
  );

  const handleColumnsToggle = useCallback(
    (columnString: AMColumn) => {
      const currentSelectedColumnsCount = localColumns.filter((column) =>
        localResolution === AMResolution.MONTHLY
          ? column !== AMColumn.AUDITED
          : true
      );

      if (
        !(
          currentSelectedColumnsCount.length === 1 &&
          currentSelectedColumnsCount.includes(columnString)
        )
      ) {
        setLocalColumns((currentColumns) => {
          const newColumns = _.xor(currentColumns, [columnString]);

          if (
            newColumns.filter((column) =>
              [AMColumn.BUDGETED, AMColumn.ACTUAL].includes(column)
            ).length === 0
          ) {
            setLocalShowYearToDateColumn(false);
          }

          return newColumns;
        });
      }
    },
    [localColumns, localResolution]
  );

  const handleVarianceDisplayModeChange = useCallback(
    (displayModeString: AMVarianceDisplayMode) => {
      setLocalVarianceDisplayMode((currentDisplayModes) =>
        _.xor(currentDisplayModes, [displayModeString])
      );
    },
    []
  );

  const isVarianceDisplayModeDisabled = useMemo(() => {
    let activeColumns = localColumns;

    if (localResolution === AMResolution.MONTHLY) {
      activeColumns = localColumns.filter(
        (column) => column !== AMColumn.AUDITED
      );
    }

    return activeColumns.length > 2;
  }, [localColumns, localResolution]);

  const allDataRows = useMemo(
    () => generateAllRowsFromNewAMDataStructure({ data }),
    [data]
  );

  const latestAMColumnData = useMemo(
    () =>
      ({ resolution }: { resolution: AMResolution }) => {
        const formatterMap: Record<
          AMResolution,
          (info: { year: number; quarter?: number; month?: number }) => string
        > = {
          [AMResolution.YEARLY]: ({ year }) => `Latest: ${year}`,
          [AMResolution.QUARTERLY]: ({ year, quarter }) =>
            `Latest: Q${quarter} ${year}`,
          [AMResolution.MONTHLY]: ({ year, month }) => {
            if (!month) return "No datasets";
            const date = new Date(2000, month - 1, 1);
            const monthName = date.toLocaleString("en-US", { month: "short" });
            return `Latest: ${monthName} ${year}`;
          },
        };

        return extractLatestDatasetByAMColumn({
          data: allDataRows,
          resolution,
          formatter: formatterMap[resolution],
        });
      },
    [allDataRows]
  );

  return (
    <Box>
      <Button
        variant="outlined"
        onClick={handleClick}
        data-testid="table-settings-button"
        startIcon={<SettingsOutlinedIcon />}
      >
        Settings
      </Button>
      <Drawer
        open={isOpen}
        onClose={handleClose}
        anchor="right"
        PaperProps={{
          sx: { width: "581px" },
        }}
        variant="temporary"
        data-testid="table-settings-sidepanel"
      >
        <SidepanelHeader>
          <Stack gap={1} direction="row" alignItems="center">
            <SettingsOutlinedIcon sx={{ color: colors.blue100 }} />
            <Text variant="h2">Table Settings</Text>
          </Stack>
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{ alignSelf: "center" }}
            dataTestid="close-button"
          >
            <CloseIcon />
          </IconButton>
        </SidepanelHeader>
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: theme.spacing(3, 5),
            gap: 2,
            flexGrow: 1,
          }}
        >
          <Stack>
            <Text marginTop={1} variant="text2" fontWeight={700}>
              Datasets & calculations
            </Text>
            <Text marginTop={1} variant="text4">
              Select the time period, the type of data, and how you would like
              the calculations to be displayed, if at all. If one data type is
              selected, you will see a period over period calculation, comparing
              the current period to the previous one (e.g. YoY). If two data
              types are selected, the variance between them will be calculated
              and displayed. If all three data types are selected, no
              calculation will be shown.
            </Text>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <Stack direction="row">
              <SettingsCategory
                label="Time period"
                options={[
                  {
                    label: AMResolution.MONTHLY,
                    value: AMResolution.MONTHLY,
                  },
                  {
                    label: AMResolution.QUARTERLY,
                    value: AMResolution.QUARTERLY,
                  },
                  { label: AMResolution.YEARLY, value: AMResolution.YEARLY },
                ]}
                value={localResolution}
                onChange={handleResolutionChange}
                dataTestid="time-format"
              />
              <SettingsCategory
                multiple
                label="Data type"
                options={[
                  {
                    label: (
                      <Stack>
                        <Text data-testid="data-type-option-name">
                          {AMColumn.BUDGETED}
                        </Text>
                        <Text variant="text4" color={colors.gray60}>
                          {localResolution === AMResolution.YEARLY &&
                            latestAMColumnData({
                              resolution: AMResolution.YEARLY,
                            }).budgeted}
                          {localResolution === AMResolution.MONTHLY &&
                            latestAMColumnData({
                              resolution: AMResolution.MONTHLY,
                            }).budgeted}
                          {localResolution === AMResolution.QUARTERLY &&
                            latestAMColumnData({
                              resolution: AMResolution.QUARTERLY,
                            }).budgeted}
                        </Text>
                      </Stack>
                    ),
                    value: AMColumn.BUDGETED,
                  },
                  {
                    label: (
                      <Stack>
                        <Text data-testid="data-type-option-name">
                          {AMColumn.ACTUAL}
                        </Text>
                        <Text variant="text4" color={colors.gray60}>
                          {localResolution === AMResolution.YEARLY &&
                            latestAMColumnData({
                              resolution: AMResolution.YEARLY,
                            }).actual}
                          {localResolution === AMResolution.MONTHLY &&
                            latestAMColumnData({
                              resolution: AMResolution.MONTHLY,
                            }).actual}
                          {localResolution === AMResolution.QUARTERLY &&
                            latestAMColumnData({
                              resolution: AMResolution.QUARTERLY,
                            }).actual}
                        </Text>
                      </Stack>
                    ),
                    value: AMColumn.ACTUAL,
                  },
                  {
                    label: (
                      <Stack>
                        <Text data-testid="data-type-option-name">
                          {AMColumn.AUDITED}
                        </Text>
                        <Text variant="text4" color={colors.gray60}>
                          {localResolution === AMResolution.YEARLY &&
                            latestAMColumnData({
                              resolution: AMResolution.YEARLY,
                            }).audited}
                          {localResolution === AMResolution.MONTHLY &&
                            latestAMColumnData({
                              resolution: AMResolution.MONTHLY,
                            }).audited}
                          {localResolution === AMResolution.QUARTERLY &&
                            latestAMColumnData({
                              resolution: AMResolution.QUARTERLY,
                            }).audited}
                        </Text>
                      </Stack>
                    ),
                    value: AMColumn.AUDITED,
                    disabled: localResolution === AMResolution.MONTHLY,
                    tooltip:
                      localResolution === AMResolution.MONTHLY
                        ? "Audited data is not available for monthly time period."
                        : undefined,
                  },
                ]}
                onChange={handleColumnsToggle}
                value={localColumns}
                dataTestid="data-type"
              />

              <SettingsCategory
                multiple
                label="Calculation"
                options={[
                  {
                    label: AMVarianceDisplayMode.ABSOLUTE,
                    value: AMVarianceDisplayMode.ABSOLUTE,
                    disabled: isVarianceDisplayModeDisabled,
                    tooltip: isVarianceDisplayModeDisabled
                      ? "Absolute value calculation is not available when one or three data types are selected."
                      : undefined,
                  },
                  {
                    label: AMVarianceDisplayMode.PERCENTAGE,
                    value: AMVarianceDisplayMode.PERCENTAGE,
                    disabled: isVarianceDisplayModeDisabled,
                    tooltip: isVarianceDisplayModeDisabled
                      ? "Percentage calculation is not available when one or three data types are selected."
                      : undefined,
                  },
                ]}
                onChange={handleVarianceDisplayModeChange}
                value={localVarianceDisplayMode}
                dataTestid="columns-comparison"
              />
            </Stack>

            <Stack direction="column">
              <Text variant="text2" fontWeight={700}>
                View settings
              </Text>
              <Text variant="text4">
                Define to what level of detail you want your datasets to be
                displayed.
              </Text>
            </Stack>

            <Stack alignItems="flex-start">
              <AssetManagementToggle
                label="Show Year-to-Date"
                checked={localShowYearToDateColumn}
                onToggleChange={() =>
                  setLocalShowYearToDateColumn((prev) => !prev)
                }
                isSettingsToggle
                dataTestid="show-hide-year-to-date"
                disabled={
                  localColumns.filter(
                    (column) =>
                      column === AMColumn.BUDGETED || column === AMColumn.ACTUAL
                  ).length === 0
                }
                tooltip={
                  localColumns.filter(
                    (column) =>
                      column === AMColumn.BUDGETED || column === AMColumn.ACTUAL
                  ).length === 0
                    ? "Year-to-Date is not available when neither Budgeted or Actual data types are selected."
                    : undefined
                }
              />
              <AssetManagementToggle
                label="Show sub-items"
                checked={localShowSubItems}
                onToggleChange={() => setLocalShowSubItems((prev) => !prev)}
                dataTestid="show-hide-table-sub-items"
                isSettingsToggle
              />
              <AssetManagementToggle
                label="Hide empty rows"
                checked={localHideEmptyRows}
                onToggleChange={() => setLocalHideEmptyRows((prev) => !prev)}
                isSettingsToggle
                dataTestid="hide-empty-rows"
              />
            </Stack>
          </Stack>
          <Stack
            direction="row"
            alignSelf="flex-end"
            sx={{ marginTop: "auto" }}
          >
            <Button
              variant="text"
              onClick={handleClose}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button onClick={handleApply} data-testid="apply-settings-button">
              Apply settings
            </Button>
          </Stack>
        </Paper>
      </Drawer>
    </Box>
  );
};

export default AssetManagementTableSettings;
